import { LockClosedIcon } from '@heroicons/react/24/solid';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { UpgradeModal } from '@/common/components/UpgradeModal';
import { useSubscriptionStore } from '@/common/stores/useSubscriptionStore';
import { AVAILABLE_WIDGETS, MAX_FREE_WIDGETS, WIDGET_CATEGORIES } from '@/common/utils/constants';

interface WidgetDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  activeWidgets: string[];
  onAddWidget: (widgetType: string) => void;
}

export const WidgetDrawer = ({
  isOpen,
  onClose,
  activeWidgets,
  onAddWidget,
}: WidgetDrawerProps) => {
  const { t } = useTranslation();
  const { isPremium, canAddWidget } = useSubscriptionStore();
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  const activeWidgetCount = activeWidgets.length;
  const atFreeLimit = !isPremium && activeWidgetCount >= MAX_FREE_WIDGETS;

  const handleAddWidget = (widgetType: string) => {
    if (!canAddWidget(widgetType, activeWidgetCount)) {
      setShowUpgradeModal(true);
      return;
    }
    onAddWidget(widgetType);
    onClose();
  };

  return (
    <>
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/30 backdrop-blur-sm transition-opacity z-50"
          onClick={onClose}
        />
      )}

      <div
        className={`fixed right-0 top-0 h-full w-96 bg-white shadow-2xl transform transition-all duration-300 ease-out z-50 ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          <div className="flex justify-between items-center p-6 border-b border-gray-200">
            <div>
              <h2 className="text-xl font-semibold text-gray-800">{t('widgetDrawer.addWidget')}</h2>
              {!isPremium && (
                <p className="text-xs text-gray-500 mt-1">
                  {t('widgetDrawer.widgetsUsed', { count: activeWidgetCount, max: MAX_FREE_WIDGETS })}
                </p>
              )}
            </div>
            <button
              className="text-gray-400 hover:text-gray-600 rounded-full p-2 hover:bg-gray-100 transition-colors"
              onClick={onClose}
            >
              <span className="sr-only">{t('common.close')}</span>✕
            </button>
          </div>

          <div className="flex-1 p-6 overflow-y-auto">
            {Object.values(WIDGET_CATEGORIES).map((category) => {
              const categoryWidgets = AVAILABLE_WIDGETS.filter(
                (widget) => widget.category === category,
              );

              if (categoryWidgets.length === 0) return null;

              return (
                <div key={category} className="mb-8 last:mb-0">
                  <h3 className="text-sm font-medium text-gray-500 mb-3 px-4">{t(`categories.${category}`)}</h3>
                  <div className="space-y-3">
                    {categoryWidgets.map((widget) => {
                      const alreadyAdded = !widget.allowMultiple && activeWidgets.includes(widget.id);
                      const isPremiumLocked = !isPremium && widget.premium;
                      const isLimitLocked = atFreeLimit && !alreadyAdded && !isPremiumLocked;
                      const blocked = alreadyAdded || isPremiumLocked || isLimitLocked;

                      return (
                        <button
                          key={widget.id}
                          className={`block w-full text-left px-4 py-4 rounded-lg transition-colors border ${
                            blocked
                              ? 'bg-gray-50 border-gray-200 cursor-not-allowed opacity-60'
                              : 'text-gray-700 hover:bg-gray-50 border-gray-200 hover:border-gray-300'
                          }`}
                          disabled={alreadyAdded}
                          onClick={() => handleAddWidget(widget.id)}
                        >
                          <div className="flex items-center gap-4">
                            <div className="p-2 bg-gray-100 rounded-md">
                              <widget.icon className="h-5 w-5 text-gray-600" />
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <h3 className="font-medium text-gray-900">{t(`widgets.${widget.id}.title`)}</h3>
                                {isPremiumLocked && (
                                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-200 text-gray-900">
                                    <LockClosedIcon className="h-3 w-3" />
                                    {t('widgetDrawer.premium')}
                                  </span>
                                )}
                              </div>
                              {alreadyAdded && (
                                <p className="text-sm text-gray-500 mt-1">{t('widgetDrawer.alreadyAdded')}</p>
                              )}
                              {isLimitLocked && (
                                <p className="text-sm text-gray-700 mt-1">{t('widgetDrawer.upgradeToAddMore')}</p>
                              )}
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>

          {!isPremium && (
            <div className="p-4 border-t border-gray-200 bg-gray-50">
              <button
                className="w-full py-2.5 px-4 rounded-lg bg-gray-900 text-white text-sm font-medium hover:bg-gray-800 transition-all shadow-sm hover:shadow-md active:scale-95"
                onClick={() => setShowUpgradeModal(true)}
              >
                {t('widgetDrawer.upgradeToPremium')}
              </button>
            </div>
          )}
        </div>
      </div>

      <UpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
      />
    </>
  );
};
